import React, { useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, Printer, Download, Maximize2, Minimize2, RotateCw, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Globaler PDF-Viewer-Modal.
 * Wird über window.dispatchEvent(new CustomEvent("mh:open-pdf", {detail: {path, filename}}))
 * aus dem lib/pdf.js Helper aufgerufen.
 *
 * Features: Zoom (+/− / Ctrl+Scroll), Drucken (browser print dialog),
 * Download, Vollbild, Rotation, elegantes Kino-Overlay.
 */
export default function PdfViewerRoot() {
  const [state, setState] = useState(null); // { path, filename }
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [rotate, setRotate] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const iframeRef = useRef(null);

  // Register global marker so lib/pdf.js weiß, dass das Modal aktiv ist
  useEffect(() => {
    window.__mhPdfModalMounted = true;
    return () => { window.__mhPdfModalMounted = false; };
  }, []);

  // Listen for global open events
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      if (detail.path) setState({ path: detail.path, filename: detail.filename || "document.pdf" });
    };
    window.addEventListener("mh:open-pdf", handler);
    return () => window.removeEventListener("mh:open-pdf", handler);
  }, []);

  // Fetch PDF blob when state changes
  useEffect(() => {
    if (!state) return;
    let cancelled = false;
    let url;
    setLoading(true);
    setZoom(100); setRotate(0); setFullscreen(false);
    api.get(state.path, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        // #view=FitH & toolbar=0 blenden PDF-Viewer-Toolbar aus, wir liefern eigene
        url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        setBlobUrl(url + "#toolbar=0&navpanes=0&view=FitH");
      })
      .catch(() => {
        if (!cancelled) toast.error("PDF konnte nicht geladen werden");
        close();
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [state]);

  // Escape to close
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if ((e.ctrlKey || e.metaKey) && e.key === "+") { e.preventDefault(); setZoom((z) => Math.min(300, z + 25)); }
      else if ((e.ctrlKey || e.metaKey) && e.key === "-") { e.preventDefault(); setZoom((z) => Math.max(50, z - 25)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state]);

  const close = () => {
    setState(null);
    if (blobUrl) URL.revokeObjectURL(blobUrl.split("#")[0]);
    setBlobUrl(null);
  };

  const doPrint = () => {
    try { iframeRef.current?.contentWindow?.print(); }
    catch { toast.error("Drucken nicht verfügbar — bitte im PDF-Viewer drucken"); }
  };

  const doDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl.split("#")[0];
    a.download = state?.filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Download gestartet");
  };

  const openNewTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl.split("#")[0], "_blank");
  };

  if (!state) return null;

  const modalCls = fullscreen
    ? "fixed inset-0 z-[100] flex flex-col bg-black/98"
    : "fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8";
  const paneCls = fullscreen
    ? "flex-1 flex flex-col overflow-hidden"
    : "w-full max-w-6xl h-[92vh] flex flex-col rounded-xl overflow-hidden border border-[#D4AF37]/30 shadow-[0_25px_80px_rgba(0,0,0,0.9)] bg-[#0A0A0A]";

  return (
    <div className={modalCls} onClick={close} data-testid="pdf-viewer-modal">
      <div className={paneCls} onClick={(e) => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#1A1A1A] bg-gradient-to-b from-[#0F0F0F] to-[#080808]">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-medium truncate">
            PDF · {state.filename}
          </div>
          <div className="flex-1" />
          {/* Zoom */}
          <div className="hidden sm:flex items-center gap-1 bg-[#050505] border border-[#1F1F1F] rounded px-1 py-0.5">
            <button onClick={() => setZoom((z) => Math.max(50, z - 25))} data-testid="pdf-zoom-out"
              className="p-1.5 hover:bg-[#D4AF37]/10 rounded text-[#A8A398] hover:text-[#D4AF37]" title="Verkleinern">
              <ZoomOut size={13} />
            </button>
            <span className="text-[10px] font-mono text-[#EEDC82] w-10 text-center">{zoom}%</span>
            <button onClick={() => setZoom((z) => Math.min(300, z + 25))} data-testid="pdf-zoom-in"
              className="p-1.5 hover:bg-[#D4AF37]/10 rounded text-[#A8A398] hover:text-[#D4AF37]" title="Vergrößern">
              <ZoomIn size={13} />
            </button>
          </div>
          {/* Rotate */}
          <button onClick={() => setRotate((r) => (r + 90) % 360)} data-testid="pdf-rotate"
            className="hidden sm:inline-flex p-1.5 border border-[#1F1F1F] rounded text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]" title="Drehen">
            <RotateCw size={13} />
          </button>
          {/* Fullscreen */}
          <button onClick={() => setFullscreen(!fullscreen)} data-testid="pdf-fullscreen"
            className="hidden sm:inline-flex p-1.5 border border-[#1F1F1F] rounded text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]" title={fullscreen ? "Fenster" : "Vollbild"}>
            {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
          {/* Neuer Tab */}
          <button onClick={openNewTab} data-testid="pdf-newtab"
            className="hidden sm:inline-flex p-1.5 border border-[#1F1F1F] rounded text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]" title="In neuem Tab">
            <ExternalLink size={13} />
          </button>
          {/* Download */}
          <button onClick={doDownload} data-testid="pdf-download"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]">
            <Download size={11} /> <span className="hidden sm:inline">Download</span>
          </button>
          {/* Print */}
          <button onClick={doPrint} data-testid="pdf-print"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-[10px] uppercase tracking-widest font-medium">
            <Printer size={11} /> <span className="hidden sm:inline">Drucken</span>
          </button>
          {/* Close */}
          <button onClick={close} data-testid="pdf-close"
            className="ml-1 p-1.5 border border-[#2A2A2A] rounded text-[#A8A398] hover:border-[#7A1921] hover:text-[#EE9CA1]" title="Schließen (Esc)">
            <X size={14} />
          </button>
        </div>

        {/* Viewer */}
        <div className="flex-1 bg-[#050505] overflow-auto relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] animate-pulse">Lade PDF …</div>
            </div>
          )}
          {blobUrl && (
            <div className="w-full h-full flex items-start justify-center p-2"
              style={{ transform: `scale(${zoom / 100})`, transformOrigin: "top center", transition: "transform 180ms ease" }}>
              <iframe
                ref={iframeRef}
                src={blobUrl}
                title="PDF-Vorschau"
                data-testid="pdf-viewer-iframe"
                className="w-full h-full min-h-[80vh] rounded border border-[#1A1A1A] bg-white"
                style={{ transform: `rotate(${rotate}deg)`, transition: "transform 240ms ease" }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
